
import {
  useState,
  useEffect,
  useRef,
  MouseEvent as ReactMouseEvent,
  CSSProperties,
  FC,
  ReactNode,
  RefObject, JSX,
} from "react";

/* ─────────────────────────────────────────────
   DESIGN TOKENS & GLOBAL STYLES
───────────────────────────────────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Oswald:wght@200;300;400;500;600;700&family=Outfit:wght@200;300;400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --bg:       #05050a;
    --bg2:      #08080f;
    --bg3:      #0c0c15;
    --card:     #0f0f1c;
    --card2:    #161625;
    --pink:     #f0055f;
    --pink2:    #ff2075;
    --sky:      #6aaddf;
    --gold:     #e2b046;
    --white:    #ffffff;
    --off:      #c5c8d8;
    --muted:    rgba(197,200,216,0.45);
    --dim:      rgba(197,200,216,0.18);
    --border:   rgba(255,255,255,0.06);
    --border2:  rgba(255,255,255,0.12);
    --nav-h:    68px;
    --f-disp:   'Oswald', sans-serif;
    --f-body:   'Outfit', sans-serif;
  }

  html { scroll-behavior: smooth; font-size: 16px; }

  body {
    font-family: var(--f-body);
    background: var(--bg);
    color: var(--white);
    overflow-x: hidden;
    -webkit-font-smoothing: antialiased;
  }

  ::-webkit-scrollbar { width: 3px; }
  ::-webkit-scrollbar-track { background: var(--bg); }
  ::-webkit-scrollbar-thumb { background: var(--pink); }

  a { color: inherit; text-decoration: none; }
  img { display: block; max-width: 100%; }
  ul { list-style: none; }
  button { cursor: pointer; border: none; background: none; font-family: inherit; }

  @keyframes ticker    { from { transform: translateX(0) } to { transform: translateX(-50%) } }
  @keyframes floatY    { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-14px) } }
  @keyframes pulse     { 0%,100% { opacity:.3;transform:scale(1) } 50% { opacity:.7;transform:scale(1.06) } }
  @keyframes fadeUp    { from { opacity:0;transform:translateY(36px) } to { opacity:1;transform:translateY(0) } }
  @keyframes fadeIn    { from { opacity:0 } to { opacity:1 } }
  @keyframes ripple    { to { transform: scale(4); opacity: 0; } }
  @keyframes shimmer   { 0% { background-position: -600px 0 } 100% { background-position: 600px 0 } }
  @keyframes spin      { to { transform: rotate(360deg) } }
  @keyframes bandScroll{ from{transform:translateX(0)} to{transform:translateX(-50%)} }

  .section      { padding: 96px 0; }
  .section-alt  { background: var(--bg2); }
  .section-dark { background: var(--bg3); }
  .container    { max-width: 1260px; margin: 0 auto; padding: 0 40px; }
  .eyebrow {
    font-family: var(--f-disp); font-size: 0.72rem; font-weight: 500;
    letter-spacing: 0.32em; text-transform: uppercase; color: var(--pink);
    display: flex; align-items: center; gap: 10px;
  }
  .eyebrow::before { content:''; display:block; width:26px; height:2px; background:var(--pink); flex-shrink:0; }

  @media (max-width: 900px) { .container { padding: 0 22px; } .section { padding: 64px 0; } }
  @media (max-width: 600px) { .section { padding: 48px 0; } }
`;

/* ─────────────────────────────────────────────
   TYPES
───────────────────────────────────────────── */
interface NavLink {
  label: string;
  id: string;
}

interface Award {
  num: string;
  lbl: string;
  color: string;
}

interface TimelineItem {
  year: string;
  event: string;
  icon: string;
}

interface Team {
  name: string;
  years: string;
  logo: string;
  apps: number;
  goals: number;
  assists: number;
  gpg: number;
}

interface Honour {
  icon: string;
  title: string;
  count: string;
  years: string[];
}

interface NewsItem {
  tag: string;
  tagColor: string;
  date: string;
  title: string;
  img: string;
  featured?: boolean;
}

interface GalleryImage {
  url: string;
  wide: boolean;
}

interface FooterCol {
  title: string;
  links: string[];
}

interface TotalStat {
  num: string;
  lbl: string;
}

interface FoundationStat {
  num: string;
  lbl: string;
}

/* ─────────────────────────────────────────────
   DATA
───────────────────────────────────────────── */
const NAV_LINKS: NavLink[] = [
  { label: "Biography",  id: "bio"        },
  { label: "Stats",      id: "stats"      },
  { label: "Honours",    id: "honours"    },
  { label: "News",       id: "news"       },
  { label: "Gallery",    id: "gallery"    },
  { label: "Foundation", id: "foundation" },
];

const TICKER: string[] = [
  "GOAT", "8× BALLON D'OR", "WORLD CHAMPION", "INTER MIAMI", "ARGENTINA",
  "897 GOALS", "COPA AMÉRICA", "6× GOLDEN SHOE", "46 TROPHIES", "MLS MVP",
];

const AWARDS: Award[] = [
  { num: "8",  lbl: "Ballon d'Or",          color: "#e2b046" },
  { num: "6",  lbl: "European Golden Shoe", color: "#e2b046" },
  { num: "4",  lbl: "Champions League",     color: "#6aaddf" },
  { num: "10", lbl: "La Liga Titles",        color: "#6aaddf" },
  { num: "1",  lbl: "World Cup",             color: "#e2b046" },
  { num: "2",  lbl: "Copa América",          color: "#f0055f" },
  { num: "7",  lbl: "Copa del Rey",          color: "#f0055f" },
  { num: "3",  lbl: "Best FIFA Player",      color: "#6aaddf" },
  { num: "4",  lbl: "Team World Cup",        color: "#e2b046" },
  { num: "1",  lbl: "Olympic Gold",          color: "#f0055f" },
];

const TIMELINE: TimelineItem[] = [
  { year: "1987", event: "Born in Rosario, Argentina",         icon: "⭐"   },
  { year: "2000", event: "Signs with FC Barcelona La Masia",   icon: "🔵🔴" },
  { year: "2004", event: "Pro debut aged 16",                   icon: "⚽"   },
  { year: "2009", event: "First Ballon d'Or & Treble",          icon: "🏆"   },
  { year: "2012", event: "Record 91 goals in a calendar year",  icon: "🎯"   },
  { year: "2021", event: "Copa América with Argentina",         icon: "🥇"   },
  { year: "2022", event: "FIFA World Cup Champion",             icon: "🌍"   },
  { year: "2023", event: "Joins Inter Miami CF",                icon: "🌴"   },
  { year: "2024", event: "Copa América & MLS MVP",              icon: "🏅"   },
];

const CLUBS: Team[] = [
  { name: "FC Barcelona",        years: "2004–2021",    logo: "https://upload.wikimedia.org/wikipedia/en/thumb/4/47/FC_Barcelona_%28crest%29.svg/80px-FC_Barcelona_%28crest%29.svg.png",    apps: 778, goals: 672, assists: 302, gpg: 0.86 },
  { name: "Paris Saint-Germain", years: "2021–2023",    logo: "https://upload.wikimedia.org/wikipedia/en/thumb/a/a7/Paris_Saint-Germain_F.C..svg/80px-Paris_Saint-Germain_F.C..svg.png",   apps: 75,  goals: 32,  assists: 35,  gpg: 0.43 },
  { name: "Inter Miami CF",      years: "2023–Present", logo: "https://upload.wikimedia.org/wikipedia/en/thumb/3/37/Inter_Miami_CF_logo_%282023%29.svg/80px-Inter_Miami_CF_logo_%282023%29.svg.png", apps: 82,  goals: 74,  assists: 41,  gpg: 0.90 },
  { name: "Argentina NT",        years: "2005–Present", logo: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/1a/24701-soccer-argentina.svg/80px-24701-soccer-argentina.svg.png",   apps: 191, goals: 114, assists: 60,  gpg: 0.60 },
];

const HONOURS: Honour[] = [
  { icon: "🏆", title: "Ballon d'Or",          count: "8×",  years: ["2009","2010","2011","2012","2015","2019","2021","2023"] },
  { icon: "🌍", title: "FIFA World Cup",        count: "1×",  years: ["2022"] },
  { icon: "⭐", title: "UEFA Champions League", count: "4×",  years: ["2006","2009","2011","2015"] },
  { icon: "🥇", title: "La Liga",               count: "10×", years: ["05","06","09","10","11","13","15","16","18","19"] },
  { icon: "🏅", title: "Copa América",          count: "2×",  years: ["2021","2024"] },
  { icon: "👟", title: "European Golden Shoe",  count: "6×",  years: ["2010","2012","2013","2017","2018","2019"] },
  { icon: "🎖", title: "Copa del Rey",          count: "7×",  years: ["2009","2012","2015","2016","2017","2018","2021"] },
  { icon: "🌟", title: "Best FIFA Player",      count: "3×",  years: ["2019","2022","2023"] },
  { icon: "🥈", title: "Olympic Gold Medal",    count: "1×",  years: ["2008"] },
];

const NEWS: NewsItem[] = [
  { tag: "MLS",        tagColor: "#f0055f", date: "Feb 20, 2026", featured: true,
    title: "Messi's Hat-Trick Fires Inter Miami Into Conference Finals",
    img: "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=900&h=540&fit=crop&q=80" },
  { tag: "Argentina",  tagColor: "#6aaddf", date: "Feb 9, 2026",
    title: "Double Strike Takes Messi's International Tally to 115",
    img: "https://images.unsplash.com/photo-1522778119026-d647f0596c20?w=500&h=320&fit=crop&q=80" },
  { tag: "Ballon d'Or",tagColor: "#e2b046", date: "Jan 28, 2026",
    title: "Behind the Scenes: The Night Messi Claimed His 8th Crown",
    img: "https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=500&h=320&fit=crop&q=80" },
];

const GALLERY_IMGS: GalleryImage[] = [
  { url: "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=700&h=700&fit=crop&q=80", wide: false },
  { url: "https://images.unsplash.com/photo-1522778119026-d647f0596c20?w=700&h=700&fit=crop&q=80", wide: false },
  { url: "https://images.unsplash.com/photo-1552058544-f2b08422138a?w=1400&h=700&fit=crop&q=80",  wide: true  },
  { url: "https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=700&h=700&fit=crop&q=80", wide: false },
  { url: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=700&h=700&fit=crop&q=80", wide: false },
];

const BIO_FACTS: [string, string][] = [
  ["Born",      "June 24, 1987"],
  ["Birthplace","Rosario, Argentina"],
  ["Position",  "Forward · No.10"],
  ["Height",    "170 cm"],
  ["Foot",      "Left"],
  ["Team",      "Inter Miami CF"],
];

const FOOTER_COLS: FooterCol[] = [
  { title: "Navigate",    links: ["Biography","Career Stats","Honours","News"] },
  { title: "More",        links: ["Gallery","Foundation","Press Kit","Contact"] },
  { title: "Follow Leo",  links: ["Instagram","Twitter / X","Facebook","YouTube"] },
];

const SECTION_IDS: string[] = ["foundation","gallery","news","honours","stats","bio","hero"];

/* ─────────────────────────────────────────────
   HOOKS
───────────────────────────────────────────── */
function useScrollY(): number {
  const [y, setY] = useState<number>(0);
  useEffect(() => {
    const handler = (): void => setY(window.scrollY);
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);
  return y;
}

function useInView(threshold = 0.15): [RefObject<HTMLDivElement>, boolean] {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState<boolean>(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) { setInView(true); obs.disconnect(); }
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref as any, inView];
}

function useCounter(target: number, active: boolean, duration = 1800): number {
  const [val, setVal] = useState<number>(0);
  useEffect(() => {
    if (!active) return;
    let start: number | null = null;
    const step = (ts: number): void => {
      if (start === null) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const ease = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(ease * target));
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, active, duration]);
  return val;
}

/* ─────────────────────────────────────────────
   BTN
───────────────────────────────────────────── */
type BtnVariant = "primary" | "outline" | "ghost" | "white";
type BtnSize = "sm" | "md";

interface BtnProps {
  children: ReactNode;
  variant?: BtnVariant;
  size?: BtnSize;
  href?: string;
  onClick?: (e: ReactMouseEvent<HTMLElement>) => void;
  style?: CSSProperties;
}

const Btn: FC<BtnProps> = ({ children, variant = "primary", size, href, onClick, style }) => {
  const base: CSSProperties = {
    position: "relative", overflow: "hidden",
    display: "inline-flex", alignItems: "center", gap: 8,
    fontFamily: "var(--f-disp)", fontWeight: 600,
    letterSpacing: "0.18em", textTransform: "uppercase",
    fontSize: size === "sm" ? "0.7rem" : "0.78rem",
    padding: size === "sm" ? "0.55em 1.3em" : "0.72em 1.9em",
    border: "2px solid transparent",
    cursor: "pointer", transition: "all 0.22s ease", outline: "none",
    textDecoration: "none", whiteSpace: "nowrap",
    ...(variant === "primary" && { background: "var(--pink)", borderColor: "var(--pink)", color: "#fff" }),
    ...(variant === "outline" && { background: "transparent", borderColor: "var(--border2)", color: "var(--off)" }),
    ...(variant === "ghost"   && { background: "rgba(255,255,255,0.05)", borderColor: "var(--border2)", color: "var(--off)" }),
    ...(variant === "white"   && { background: "#fff", borderColor: "#fff", color: "var(--pink)" }),
    ...style,
  };

  const addRipple = (e: ReactMouseEvent<HTMLElement>): void => {
    const el = e.currentTarget;
    const r = el.getBoundingClientRect();
    const sz = Math.max(r.width, r.height) * 2;
    const span = document.createElement("span");
    Object.assign(span.style, {
      position: "absolute", borderRadius: "50%", pointerEvents: "none",
      width: `${sz}px`, height: `${sz}px`,
      left: `${e.clientX - r.left - sz / 2}px`,
      top: `${e.clientY - r.top - sz / 2}px`,
      background: "rgba(255,255,255,0.22)",
      transform: "scale(0)",
      animation: "ripple 0.55s linear",
    });
    el.appendChild(span);
    span.addEventListener("animationend", () => span.remove());
  };

  if (href) {
    return (
      <a href={href} style={base} onClick={(e) => { addRipple(e); onClick?.(e); }}>
        {children}
      </a>
    );
  }
  return (
    <button style={base} onClick={(e) => { addRipple(e); onClick?.(e); }}>
      {children}
    </button>
  );
};

/* ─────────────────────────────────────────────
   TAG BADGE
───────────────────────────────────────────── */
interface TagBadgeProps {
  children: ReactNode;
  color?: string;
}

const TagBadge: FC<TagBadgeProps> = ({ children, color = "var(--pink)" }) => (
  <span style={{
    display: "inline-flex", alignItems: "center",
    fontFamily: "var(--f-disp)", fontSize: "0.65rem", fontWeight: 600,
    letterSpacing: "0.18em", textTransform: "uppercase",
    background: color,
    color: color === "var(--pink)" ? "#fff" : "#07050e",
    padding: "3px 10px",
  }}>
    {children}
  </span>
);

/* ─────────────────────────────────────────────
   NAVBAR
───────────────────────────────────────────── */
interface NavbarProps {
  activeSection: string;
}

const Navbar: FC<NavbarProps> = ({ activeSection }) => {
  const scrollY = useScrollY();
  const [mobileOpen, setMobileOpen] = useState<boolean>(false);
  const scrolled = scrollY > 50;

  return (
    <>
      <style>{`
        .nb-link { position:relative; font-family:var(--f-disp); font-size:0.76rem; font-weight:500; letter-spacing:0.16em; text-transform:uppercase; color:var(--muted); padding:8px 13px; cursor:pointer; transition:color 0.2s; white-space:nowrap; text-decoration:none; }
        .nb-link::after { content:''; position:absolute; bottom:0; left:50%; right:50%; height:2px; background:var(--pink); transition:left 0.25s,right 0.25s; }
        .nb-link:hover, .nb-link.active { color:var(--white); }
        .nb-link:hover::after, .nb-link.active::after { left:13px; right:13px; }
        @media(max-width:900px){ .nb-desktop { display:none!important; } }
        @media(min-width:901px){ .nb-ham { display:none!important; } }
      `}</style>

      <header style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
        height: "var(--nav-h)", display: "flex", alignItems: "center",
        background: scrolled ? "rgba(5,5,10,0.97)" : "rgba(5,5,10,0.82)",
        backdropFilter: "blur(24px) saturate(180%)",
        borderBottom: "1px solid rgba(255,255,255,0.04)",
        transition: "background 0.3s, box-shadow 0.3s",
        boxShadow: scrolled ? "0 4px 48px rgba(0,0,0,0.6)" : "none",
      }}>
        <div style={{ maxWidth: 1260, margin: "0 auto", padding: "0 40px", width: "100%", display: "flex", alignItems: "center" }}>
          <a href="#hero" style={{ display: "flex", alignItems: "center", gap: 11, marginRight: "auto", flexShrink: 0, textDecoration: "none" }}>
            <div style={{ width: 36, height: 36, borderRadius: "50%", background: "var(--pink)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--f-disp)", fontSize: "1.15rem", fontWeight: 700, color: "#fff", flexShrink: 0 }}>M</div>
            <div>
              <div style={{ fontFamily: "var(--f-disp)", fontSize: "0.58rem", fontWeight: 400, letterSpacing: "0.32em", textTransform: "uppercase", color: "var(--muted)", lineHeight: 1 }}>Lionel</div>
              <div style={{ fontFamily: "var(--f-disp)", fontSize: "1.1rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--white)", lineHeight: 1 }}>MESSI</div>
            </div>
          </a>

          <nav className="nb-desktop" style={{ display: "flex", alignItems: "center", gap: 2 }}>
            {NAV_LINKS.map((l) => (
              <a key={l.id} className={`nb-link${activeSection === l.id ? " active" : ""}`} href={`#${l.id}`}>{l.label}</a>
            ))}
          </nav>

          <div className="nb-desktop" style={{ display: "flex", alignItems: "center", gap: 10, marginLeft: 20 }}>
            <span style={{ fontFamily: "var(--f-disp)", fontSize: "0.7rem", fontWeight: 600, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--muted)", padding: "4px 8px", border: "1px solid var(--border2)", cursor: "pointer" }}>EN</span>
            <Btn size="sm">Official Store</Btn>
          </div>

          <button className="nb-ham" onClick={() => setMobileOpen((o) => !o)} style={{ marginLeft: "auto", display: "flex", flexDirection: "column", gap: 5, padding: 4 }} aria-label="Menu">
            {([0, 1, 2] as const).map((i) => (
              <span key={i} style={{
                display: "block", width: 22, height: 2, background: "var(--white)", transition: "0.3s",
                ...(mobileOpen && i === 0 && { transform: "translateY(7px) rotate(45deg)" }),
                ...(mobileOpen && i === 1 && { opacity: 0 }),
                ...(mobileOpen && i === 2 && { transform: "translateY(-7px) rotate(-45deg)" }),
              }} />
            ))}
          </button>
        </div>
      </header>

      {mobileOpen && (
        <div style={{ position: "fixed", top: "var(--nav-h)", left: 0, right: 0, zIndex: 999, background: "rgba(5,5,10,0.98)", backdropFilter: "blur(20px)", borderBottom: "1px solid var(--border)", padding: "16px 22px 24px", display: "flex", flexDirection: "column", gap: 4 }}>
          {NAV_LINKS.map((l) => (
            <a key={l.id} className="nb-link" href={`#${l.id}`} onClick={() => setMobileOpen(false)} style={{ fontSize: "0.9rem", padding: "10px 0" }}>{l.label}</a>
          ))}
        </div>
      )}
    </>
  );
};

/* ─────────────────────────────────────────────
   HERO STAT
───────────────────────────────────────────── */
interface HeroStatProps {
  label: string;
  value: number | string;
  suffix?: string;
}

const HeroStat: FC<HeroStatProps> = ({ label, value, suffix = "" }) => {
  const [ref, inView] = useInView(0.1);
  const numericTarget = typeof value === "number" ? value : 0;
  const count = useCounter(numericTarget, inView, 2000);

  return (
    <div ref={ref} style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", paddingRight: 20, borderRight: "3px solid rgba(240,5,95,0.55)" }}>
      <span style={{ fontFamily: "var(--f-disp)", fontSize: "clamp(42px,5.5vw,76px)", fontWeight: 700, lineHeight: 1, color: "var(--white)" }}>
        {typeof value === "number" ? count : value}{suffix}
      </span>
      <span style={{ fontFamily: "var(--f-disp)", fontSize: "0.68rem", fontWeight: 400, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--muted)" }}>{label}</span>
    </div>
  );
};

/* ─────────────────────────────────────────────
   HERO SECTION
───────────────────────────────────────────── */
const Hero: FC = () => (
  <section id="hero" style={{ position: "relative", minHeight: "100svh", overflow: "hidden", display: "flex", flexDirection: "column", paddingTop: "var(--nav-h)" }}>
    {/* Backgrounds */}
    <div style={{ position: "absolute", inset: 0, zIndex: 0, background: "radial-gradient(ellipse 70% 60% at 65% 25%, rgba(240,5,95,0.13) 0%, transparent 70%), radial-gradient(ellipse 55% 55% at 15% 88%, rgba(106,173,223,0.07) 0%, transparent 65%), var(--bg)" }} />
    <div style={{ position: "absolute", inset: 0, zIndex: 1, opacity: 0.035, pointerEvents: "none", backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 512 512' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`, backgroundSize: "200px" }} />
    <div style={{ position: "absolute", inset: 0, zIndex: 1, opacity: 0.025, pointerEvents: "none", backgroundImage: "linear-gradient(rgba(255,255,255,1) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,1) 1px,transparent 1px)", backgroundSize: "72px 72px" }} />
    <div style={{ position: "absolute", right: "-3%", top: "0%", zIndex: 1, fontFamily: "var(--f-disp)", fontSize: "clamp(200px,28vw,540px)", fontWeight: 700, lineHeight: 1, color: "transparent", WebkitTextStroke: "2px rgba(240,5,95,0.055)", pointerEvents: "none", userSelect: "none", animation: "pulse 9s ease-in-out infinite" }}>10</div>

    {/* Body grid */}
    <div style={{ position: "relative", zIndex: 2, flex: 1, display: "grid", gridTemplateColumns: "1fr minmax(0,430px) 1fr", alignItems: "center", gap: 20, padding: "40px 80px 110px" }}>
      {/* Left — copy */}
      <div style={{ display: "flex", flexDirection: "column", gap: 0, animation: "fadeUp 0.8s 0.1s both" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18, fontFamily: "var(--f-disp)", fontSize: "0.72rem", fontWeight: 500, letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--muted)" }}>
          🇦🇷 Inter Miami · Argentina NT
        </div>
        <h1 style={{ fontFamily: "var(--f-disp)", fontWeight: 700, textTransform: "uppercase", lineHeight: 0.88, marginBottom: 28 }}>
          <span style={{ display: "block", fontSize: "clamp(38px,5.5vw,88px)", letterSpacing: "0.22em", fontWeight: 200, color: "rgba(255,255,255,0.5)" }}>Lionel</span>
          <span style={{ display: "block", fontSize: "clamp(78px,13vw,200px)", letterSpacing: "-0.025em", background: "linear-gradient(120deg,#fff 35%,var(--pink))", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>MESSI</span>
        </h1>
        <p style={{ fontSize: "0.93rem", fontWeight: 300, lineHeight: 1.78, color: "var(--muted)", maxWidth: 380, marginBottom: 32 }}>
          Eight-time Ballon d'Or winner. World Champion with Argentina. The greatest footballer to ever grace the beautiful game.
        </p>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <Btn href="#bio">Explore Story</Btn>
          <Btn variant="outline" href="#foundation">Foundation</Btn>
        </div>
      </div>

      {/* Centre — player */}
      <div style={{ position: "relative", display: "flex", alignItems: "flex-end", justifyContent: "center", height: "clamp(300px,58vh,640px)" }}>
        <div style={{ position: "absolute", bottom: -40, left: "50%", transform: "translateX(-50%)", width: 280, height: 280, borderRadius: "50%", background: "radial-gradient(circle,rgba(240,5,95,0.38) 0%,transparent 70%)", filter: "blur(50px)", pointerEvents: "none" }} />
        {([300, 440] as const).map((s, i) => (
          <div key={i} style={{ position: "absolute", width: s, height: s, borderRadius: "50%", border: "1px solid rgba(240,5,95,0.1)", bottom: i === 0 ? -40 : -110, left: "50%", transform: "translateX(-50%)", animation: `pulse ${4 + i * 2}s ${i * 2}s ease-in-out infinite`, pointerEvents: "none" }} />
        ))}
        <img
          src="https://upload.wikimedia.org/wikipedia/commons/thumb/c/c1/Lionel_Messi_20180626.jpg/600px-Lionel_Messi_20180626.jpg"
          alt="Lionel Messi"
          style={{ position: "relative", zIndex: 2, width: "100%", height: "100%", objectFit: "cover", objectPosition: "top center", maskImage: "linear-gradient(to bottom, black 55%, transparent 100%)", WebkitMaskImage: "linear-gradient(to bottom, black 55%, transparent 100%)", filter: "drop-shadow(0 0 50px rgba(240,5,95,0.35))", animation: "floatY 7s ease-in-out infinite" }}
        />
      </div>

      {/* Right — stats */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 28, animation: "fadeUp 0.8s 0.3s both" }}>
        <HeroStat value={897} label="Career Goals" />
        <HeroStat value={380} label="Career Assists" />
        <HeroStat value="8"   label="Ballon d'Or" suffix="×" />
        <HeroStat value="1"   label="World Cup" />
      </div>
    </div>

    {/* Scroll cue */}
    <div style={{ position: "absolute", bottom: 60, left: "50%", transform: "translateX(-50%)", zIndex: 3, display: "flex", flexDirection: "column", alignItems: "center", gap: 8, fontFamily: "var(--f-disp)", fontSize: "0.62rem", letterSpacing: "0.32em", textTransform: "uppercase", color: "var(--dim)" }}>
      <div style={{ width: 1, height: 46, background: "linear-gradient(var(--pink),transparent)", animation: "floatY 2.5s ease-in-out infinite" }} />
      <span>Scroll</span>
    </div>

    {/* Ticker */}
    <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, zIndex: 4, height: 46, background: "var(--pink)", overflow: "hidden", display: "flex", alignItems: "center" }}>
      <div style={{ display: "flex", whiteSpace: "nowrap", animation: "ticker 30s linear infinite" }}>
        {[...TICKER, ...TICKER, ...TICKER].map((t, i) => (
          <span key={i} style={{ fontFamily: "var(--f-disp)", fontSize: "0.85rem", fontWeight: 600, letterSpacing: "0.22em", textTransform: "uppercase", padding: "0 22px", color: i % 2 === 0 ? "rgba(255,255,255,0.95)" : "rgba(255,255,255,0.4)" }}>{t}</span>
        ))}
      </div>
    </div>

    <style>{`
      @media(max-width:1100px){
        #hero > div:nth-child(5){ grid-template-columns:1fr!important; padding:28px 28px 90px!important; justify-items:center; }
      }
    `}</style>
  </section>
);

/* ─────────────────────────────────────────────
   AWARDS BAND
───────────────────────────────────────────── */
const AwardsBand: FC = () => {
  const doubled = [...AWARDS, ...AWARDS];
  return (
    <div style={{ background: "linear-gradient(135deg,#0c031a 0%,#180630 100%)", borderTop: "1px solid rgba(240,5,95,0.2)", borderBottom: "1px solid rgba(240,5,95,0.1)", overflow: "hidden", position: "relative" }}>
      {(["left", "right"] as const).map((side) => (
        <div key={side} style={{ position: "absolute", top: 0, bottom: 0, width: 80, zIndex: 2, pointerEvents: "none", [side]: 0, background: `linear-gradient(to ${side === "left" ? "right" : "left"},#0c031a,transparent)` }} />
      ))}
      <div style={{ display: "flex", width: "max-content", animation: "bandScroll 55s linear infinite" }}>
        {doubled.map((a, i) => (
          <div key={i} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, padding: "16px 38px", borderRight: "1px solid rgba(255,255,255,0.04)", minWidth: 160 }}>
            <div style={{ fontFamily: "var(--f-disp)", fontSize: "2rem", fontWeight: 700, lineHeight: 1, color: a.color }}>{a.num}</div>
            <div style={{ fontFamily: "var(--f-disp)", fontSize: "0.62rem", fontWeight: 400, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted)", whiteSpace: "nowrap" }}>{a.lbl}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   BIOGRAPHY
───────────────────────────────────────────── */
const Bio: FC = () => {
  const [ref, inView] = useInView();
  return (
    <section id="bio" className="section section-alt">
      <div className="container">
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 72, alignItems: "center" }}>
          {/* Image column */}
          <div ref={ref} style={{ position: "relative", opacity: inView ? 1 : 0, transform: inView ? "none" : "translateX(-40px)", transition: "opacity 0.7s,transform 0.7s" }}>
            <div style={{ position: "relative", aspectRatio: "3/4", overflow: "hidden" }}>
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/thumb/b/b4/Lionel-Messi-Argentina-2022-FIFA-World-Cup_%28cropped%29.jpg/600px-Lionel-Messi-Argentina-2022-FIFA-World-Cup_%28cropped%29.jpg"
                alt="Messi with World Cup 2022"
                style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.7s ease" }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLImageElement).style.transform = "scale(1.04)"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLImageElement).style.transform = "scale(1)"; }}
              />
              <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to top, rgba(5,5,10,0.82) 0%, transparent 55%)" }} />
              <div style={{ position: "absolute", bottom: 24, left: 24, background: "var(--pink)", padding: "12px 18px" }}>
                <div style={{ fontFamily: "var(--f-disp)", fontSize: "2.1rem", fontWeight: 700, color: "#fff", lineHeight: 1 }}>8×</div>
                <div style={{ fontFamily: "var(--f-disp)", fontSize: "0.6rem", fontWeight: 600, letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(255,255,255,0.8)" }}>Ballon d'Or</div>
              </div>
            </div>
            <div style={{ position: "absolute", top: -16, left: -16, width: 88, height: 88, border: "2px solid rgba(240,5,95,0.22)", zIndex: -1 }} />
          </div>

          {/* Content column */}
          <div style={{ display: "flex", flexDirection: "column", gap: 22, opacity: inView ? 1 : 0, transform: inView ? "none" : "translateX(40px)", transition: "opacity 0.7s 0.15s,transform 0.7s 0.15s" }}>
            <div className="eyebrow">Biography</div>
            <h2 style={{ fontFamily: "var(--f-disp)", fontSize: "clamp(40px,6vw,88px)", fontWeight: 700, textTransform: "uppercase", lineHeight: 0.88 }}>
              The Legend<br />Begins
            </h2>
            <p style={{ fontSize: "0.93rem", fontWeight: 300, lineHeight: 1.8, color: "var(--muted)" }}>
              Born June 24, 1987 in Rosario, Argentina, Lionel Andrés Messi was diagnosed with a growth hormone deficiency at age ten. FC Barcelona offered to fund his treatment — scribbling the deal on a napkin — and the greatest career in football history quietly began.
            </p>
            <p style={{ fontSize: "0.93rem", fontWeight: 300, lineHeight: 1.8, color: "var(--muted)" }}>
              After 17 unrivalled seasons at the Camp Nou, Messi fulfilled his ultimate dream in Qatar 2022 by lifting the FIFA World Cup as Argentina's captain. Today, wearing his iconic number 10 for Inter Miami, he continues rewriting every record in the sport.
            </p>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", border: "1px solid var(--border)", overflow: "hidden" }}>
              {BIO_FACTS.map(([k, v], i) => (
                <div key={i} style={{ padding: "12px 15px", borderBottom: i < 4 ? "1px solid var(--border)" : "none", borderRight: i % 2 === 0 ? "1px solid var(--border)" : "none" }}>
                  <div style={{ fontFamily: "var(--f-disp)", fontSize: "0.62rem", fontWeight: 600, letterSpacing: "0.2em", textTransform: "uppercase", color: "var(--pink)", marginBottom: 4 }}>{k}</div>
                  <div style={{ fontSize: "0.9rem", fontWeight: 500 }}>{v}</div>
                </div>
              ))}
            </div>
            <div><Btn href="#stats">View Career Stats</Btn></div>
          </div>
        </div>

        {/* Timeline */}
        <div style={{ marginTop: 72 }}>
          <div className="eyebrow" style={{ marginBottom: 32 }}>Career Journey</div>
          <div style={{ display: "flex", overflowX: "auto", paddingBottom: 16, gap: 0, scrollbarWidth: "thin", scrollbarColor: "var(--pink) var(--bg)" }}>
            {TIMELINE.map((t, i) => (
              <div key={i} style={{ flex: "1", minWidth: 128, display: "flex", flexDirection: "column", alignItems: "center", gap: 10, textAlign: "center", position: "relative", paddingTop: 6 }}>
                {i < TIMELINE.length - 1 && (
                  <div style={{ position: "absolute", top: 24, left: "50%", width: "100%", height: 2, background: "linear-gradient(to right,rgba(240,5,95,0.4),rgba(240,5,95,0.08))" }} />
                )}
                <div style={{ width: 40, height: 40, borderRadius: "50%", background: "var(--card)", border: "2px solid rgba(240,5,95,0.28)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1rem", position: "relative", zIndex: 1, cursor: "default", transition: "border-color 0.25s,background 0.25s" }}
                  onMouseEnter={(e) => { const el = e.currentTarget as HTMLDivElement; el.style.borderColor = "var(--pink)"; el.style.background = "rgba(240,5,95,0.15)"; }}
                  onMouseLeave={(e) => { const el = e.currentTarget as HTMLDivElement; el.style.borderColor = "rgba(240,5,95,0.28)"; el.style.background = "var(--card)"; }}
                >{t.icon}</div>
                <div style={{ fontFamily: "var(--f-disp)", fontSize: "1.5rem", fontWeight: 700 }}>{t.year}</div>
                <div style={{ fontSize: "0.7rem", fontWeight: 300, color: "var(--muted)", lineHeight: 1.4, padding: "0 4px" }}>{t.event}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <style>{`@media(max-width:900px){#bio .container > div:first-child{grid-template-columns:1fr!important;gap:36px!important}}`}</style>
    </section>
  );
};

/* ─────────────────────────────────────────────
   STATS — CLUB CARD
───────────────────────────────────────────── */
interface TeamCardProps {
  club: Team;
  visible: boolean;
}

const TeamCard: FC<TeamCardProps> = ({ club, visible }) => {
  const gpgPct = Math.min(100, club.gpg * 100);
  const statRows: [string, number][] = [["Apps", club.apps], ["Goals", club.goals], ["Assists", club.assists]];

  return (
    <div style={{ flex: "0 0 calc(33.333% - 14px)", background: "var(--card)", border: "1px solid var(--border)", padding: 24, transition: "border-color 0.25s,transform 0.25s", cursor: "default", opacity: visible ? 1 : 0 }}
      onMouseEnter={(e) => { const el = e.currentTarget as HTMLDivElement; el.style.borderColor = "rgba(240,5,95,0.35)"; el.style.transform = "translateY(-6px)"; }}
      onMouseLeave={(e) => { const el = e.currentTarget as HTMLDivElement; el.style.borderColor = "var(--border)"; el.style.transform = "none"; }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18 }}>
        <img src={club.logo} alt={club.name} style={{ width: 46, height: 46, objectFit: "contain" }}
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
        <div>
          <div style={{ fontFamily: "var(--f-disp)", fontSize: "1.05rem", fontWeight: 700 }}>{club.name}</div>
          <div style={{ fontFamily: "var(--f-disp)", fontSize: "0.68rem", fontWeight: 600, letterSpacing: "0.1em", color: "var(--pink)" }}>{club.years}</div>
        </div>
      </div>
      <div style={{ display: "flex", borderTop: "1px solid var(--border)" }}>
        {statRows.map(([lbl, val]) => (
          <div key={lbl} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 2, padding: "11px 8px", borderRight: lbl !== "Assists" ? "1px solid var(--border)" : "none" }}>
            <span style={{ fontFamily: "var(--f-disp)", fontSize: "1.65rem", fontWeight: 700, lineHeight: 1 }}>{val}</span>
            <span style={{ fontFamily: "var(--f-disp)", fontSize: "0.6rem", fontWeight: 400, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--muted)" }}>{lbl}</span>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: "0.66rem", color: "var(--muted)", marginBottom: 5 }}>{club.gpg.toFixed(2)} goals per game</div>
        <div style={{ height: 3, background: "var(--border2)", overflow: "hidden" }}>
          <div style={{ height: "100%", background: "var(--pink)", width: visible ? `${gpgPct}%` : "0%", transition: "width 1.2s cubic-bezier(0.25,0.46,0.45,0.94)" }} />
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   STATS SECTION
───────────────────────────────────────────── */
const Stats: FC = () => {
  const [page, setPage] = useState<number>(0);
  const [ref, inView] = useInView();
  const total = Math.ceil(CLUBS.length / 3);

  useEffect(() => {
    if (!inView) return;
    const t = setInterval(() => setPage((p) => (p + 1) % total), 5000);
    return () => clearInterval(t);
  }, [inView, total]);

  const TOTALS: TotalStat[] = [
    { num: "1130+", lbl: "Career Apps" },
    { num: "897+",  lbl: "Career Goals" },
    { num: "380+",  lbl: "Career Assists" },
    { num: "46",    lbl: "Team Trophies" },
  ];

  return (
    <section id="stats" className="section">
      <div className="container">
        <div style={{ marginBottom: 48, display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
          <div>
            <div className="eyebrow">Numbers</div>
            <h2 style={{ fontFamily: "var(--f-disp)", fontSize: "clamp(48px,7vw,102px)", fontWeight: 700, textTransform: "uppercase", lineHeight: 0.88 }}>Career<br />Stats</h2>
          </div>
          <TagBadge color="#e2b046">Updated Feb 2026</TagBadge>
        </div>

        <div ref={ref} style={{ position: "relative", overflow: "hidden" }}>
          <div style={{ display: "flex", gap: 20, transition: "transform 0.45s cubic-bezier(0.25,0.46,0.45,0.94)", transform: `translateX(calc(-${page * 100}%))` }}>
            {CLUBS.map((c, i) => <TeamCard key={i} club={c} visible={inView} />)}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 22, justifyContent: "center" }}>
          <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
            style={{ width: 42, height: 42, background: "var(--card)", border: "1px solid var(--border2)", color: "var(--white)", fontSize: "1.25rem", display: "flex", alignItems: "center", justifyContent: "center", cursor: page === 0 ? "not-allowed" : "pointer", opacity: page === 0 ? 0.3 : 1, transition: "background 0.2s" }}>‹</button>
          {Array.from({ length: total }, (_, i) => (
            <button key={i} onClick={() => setPage(i)}
              style={{ width: 7, height: 7, borderRadius: "50%", background: i === page ? "var(--pink)" : "rgba(255,255,255,0.18)", border: "none", cursor: "pointer", transition: "background 0.25s,transform 0.25s", transform: i === page ? "scale(1.4)" : "scale(1)" }} />
          ))}
          <button onClick={() => setPage((p) => Math.min(total - 1, p + 1))} disabled={page === total - 1}
            style={{ width: 42, height: 42, background: "var(--card)", border: "1px solid var(--border2)", color: "var(--white)", fontSize: "1.25rem", display: "flex", alignItems: "center", justifyContent: "center", cursor: page === total - 1 ? "not-allowed" : "pointer", opacity: page === total - 1 ? 0.3 : 1, transition: "background 0.2s" }}>›</button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 18, marginTop: 56 }}>
          {TOTALS.map((t, i) => (
            <div key={i} style={{ background: "var(--card)", border: "1px solid var(--border)", borderTop: "3px solid var(--pink)", padding: 24, textAlign: "center", transition: "transform 0.25s", cursor: "default" }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.transform = "translateY(-5px)"; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.transform = "none"; }}
            >
              <div style={{ fontFamily: "var(--f-disp)", fontSize: "clamp(36px,4.5vw,60px)", fontWeight: 700, lineHeight: 1 }}>{t.num}</div>
              <div style={{ fontFamily: "var(--f-disp)", fontSize: "0.68rem", fontWeight: 400, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--muted)", marginTop: 6 }}>{t.lbl}</div>
            </div>
          ))}
        </div>
      </div>
      <style>{`@media(max-width:900px){#stats .container>div:last-child{grid-template-columns:repeat(2,1fr)!important}}`}</style>
    </section>
  );
};

/* ─────────────────────────────────────────────
   HONOURS
───────────────────────────────────────────── */
const Honours: FC = () => {
  const [ref, inView] = useInView();
  return (
    <section id="honours" className="section section-dark">
      <div className="container">
        <div style={{ marginBottom: 48, display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
          <div>
            <div className="eyebrow">Trophies</div>
            <h2 style={{ fontFamily: "var(--f-disp)", fontSize: "clamp(48px,7vw,102px)", fontWeight: 700, textTransform: "uppercase", lineHeight: 0.88 }}>Honours &amp;<br />Achievements</h2>
          </div>
          <TagBadge color="#e2b046">46 Team Trophies</TagBadge>
        </div>

        <div ref={ref} style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 18 }}>
          {HONOURS.map((h, i) => (
            <div key={i} style={{
              background: "var(--card)", border: "1px solid var(--border)", padding: 22,
              opacity: inView ? 1 : 0, transform: inView ? "none" : "translateY(24px)",
              transition: `opacity 0.55s ${i * 0.06}s, transform 0.55s ${i * 0.06}s, border-color 0.2s, background 0.2s`,
              cursor: "default",
            }}
              onMouseEnter={(e) => { const el = e.currentTarget as HTMLDivElement; el.style.borderColor = "rgba(240,5,95,0.35)"; el.style.background = "rgba(240,5,95,0.04)"; }}
              onMouseLeave={(e) => { const el = e.currentTarget as HTMLDivElement; el.style.borderColor = "var(--border)"; el.style.background = "var(--card)"; }}
            >
              <div style={{ fontSize: "1.85rem", marginBottom: 8 }}>{h.icon}</div>
              <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10 }}>
                <div style={{ fontFamily: "var(--f-disp)", fontSize: "1rem", fontWeight: 700 }}>{h.title}</div>
                <div style={{ fontFamily: "var(--f-disp)", fontSize: "1.3rem", fontWeight: 700, color: "var(--pink)" }}>{h.count}</div>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                {h.years.map((y) => (
                  <span key={y} style={{ fontFamily: "var(--f-disp)", fontSize: "0.65rem", fontWeight: 600, background: "rgba(255,255,255,0.06)", padding: "2px 8px", color: "var(--off)" }}>{y}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      <style>{`@media(max-width:900px){#honours .container>div:last-child{grid-template-columns:repeat(2,1fr)!important}} @media(max-width:540px){#honours .container>div:last-child{grid-template-columns:1fr!important}}`}</style>
    </section>
  );
};

/* ─────────────────────────────────────────────
   NEWS
───────────────────────────────────────────── */
const NewsSection: FC = () => {
  const featured = NEWS.find((n) => n.featured);
  const side = NEWS.filter((n) => !n.featured);

  if (!featured) return null;

  return (
    <section id="news" className="section section-alt">
      <div className="container">
        <div style={{ marginBottom: 44, display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
          <div>
            <div className="eyebrow">Latest</div>
            <h2 style={{ fontFamily: "var(--f-disp)", fontSize: "clamp(48px,7vw,102px)", fontWeight: 700, textTransform: "uppercase", lineHeight: 0.88 }}>News</h2>
          </div>
          <Btn variant="ghost" size="sm" href="#news">All Articles →</Btn>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1.65fr 1fr", gap: 20 }}>
          {/* Featured */}
          <div style={{ position: "relative", overflow: "hidden", cursor: "pointer", aspectRatio: "16/9" }}
            onMouseEnter={(e) => { const img = (e.currentTarget as HTMLDivElement).querySelector("img"); if (img) img.style.transform = "scale(1.05)"; }}
            onMouseLeave={(e) => { const img = (e.currentTarget as HTMLDivElement).querySelector("img"); if (img) img.style.transform = "scale(1)"; }}
          >
            <img src={featured.img} alt={featured.title} style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.6s ease" }} />
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(transparent 20%,rgba(5,5,10,0.92))", display: "flex", flexDirection: "column", justifyContent: "flex-end", padding: 26 }}>
              <TagBadge color={featured.tagColor}>{featured.tag}</TagBadge>
              <div style={{ fontFamily: "var(--f-disp)", fontSize: "1.35rem", fontWeight: 600, color: "var(--white)", lineHeight: 1.25, margin: "8px 0 6px" }}>{featured.title}</div>
              <div style={{ fontSize: "0.68rem", fontWeight: 300, color: "var(--muted)", letterSpacing: "0.08em" }}>{featured.date}</div>
            </div>
          </div>

          {/* Side cards */}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {side.map((n, i) => (
              <div key={i} style={{ display: "flex", gap: 14, background: "var(--card)", border: "1px solid var(--border)", overflow: "hidden", cursor: "pointer", borderLeft: "3px solid transparent", transition: "border-left-color 0.2s,background 0.2s" }}
                onMouseEnter={(e) => { const el = e.currentTarget as HTMLDivElement; el.style.borderLeftColor = "var(--pink)"; el.style.background = "rgba(240,5,95,0.03)"; }}
                onMouseLeave={(e) => { const el = e.currentTarget as HTMLDivElement; el.style.borderLeftColor = "transparent"; el.style.background = "var(--card)"; }}
              >
                <img src={n.img} alt={n.title} style={{ width: 86, height: 66, objectFit: "cover", flexShrink: 0 }} />
                <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", justifyContent: "center", gap: 4 }}>
                  <TagBadge color={n.tagColor}>{n.tag}</TagBadge>
                  <div style={{ fontFamily: "var(--f-disp)", fontSize: "0.88rem", fontWeight: 600, color: "var(--white)", lineHeight: 1.3 }}>{n.title}</div>
                  <div style={{ fontSize: "0.66rem", color: "var(--muted)" }}>{n.date}</div>
                </div>
              </div>
            ))}
            <Btn variant="ghost" size="sm" href="#news">All News →</Btn>
          </div>
        </div>
      </div>
      <style>{`@media(max-width:900px){#news .container>div:last-child{grid-template-columns:1fr!important}}`}</style>
    </section>
  );
};

/* ─────────────────────────────────────────────
   GALLERY
───────────────────────────────────────────── */
const Gallery: FC = () => (
  <section id="gallery" className="section">
    <div className="container">
      <div style={{ marginBottom: 44, display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
        <div>
          <div className="eyebrow">Photos &amp; Videos</div>
          <h2 style={{ fontFamily: "var(--f-disp)", fontSize: "clamp(48px,7vw,102px)", fontWeight: 700, textTransform: "uppercase", lineHeight: 0.88 }}>Gallery</h2>
        </div>
        <Btn variant="ghost" size="sm" href="#gallery">View All →</Btn>
      </div>
    </div>
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 4 }}>
      {GALLERY_IMGS.map((g, i) => (
        <div key={i} style={{ position: "relative", overflow: "hidden", cursor: "pointer", aspectRatio: g.wide ? "2/1" : "1/1", gridColumn: g.wide ? "span 2" : "span 1" }}
          onMouseEnter={(e) => {
            const el = e.currentTarget as HTMLDivElement;
            const img = el.querySelector("img") as HTMLImageElement | null;
            const veil = el.querySelector(".gveil") as HTMLDivElement | null;
            if (img) { img.style.transform = "scale(1.08)"; img.style.filter = "brightness(1.14)"; }
            if (veil) veil.style.background = "rgba(240,5,95,0.18)";
          }}
          onMouseLeave={(e) => {
            const el = e.currentTarget as HTMLDivElement;
            const img = el.querySelector("img") as HTMLImageElement | null;
            const veil = el.querySelector(".gveil") as HTMLDivElement | null;
            if (img) { img.style.transform = "scale(1)"; img.style.filter = "brightness(1)"; }
            if (veil) veil.style.background = "rgba(240,5,95,0)";
          }}
        >
          <img src={g.url} alt={`Gallery ${i + 1}`} loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.55s ease,filter 0.35s" }} />
          <div className="gveil" style={{ position: "absolute", inset: 0, background: "rgba(240,5,95,0)", transition: "background 0.35s", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: "2rem", color: "#fff" }}>+</span>
          </div>
        </div>
      ))}
    </div>
    <style>{`@media(max-width:768px){#gallery~div{grid-template-columns:repeat(2,1fr)!important}}`}</style>
  </section>
);

/* ─────────────────────────────────────────────
   FOUNDATION
───────────────────────────────────────────── */
const Foundation: FC = () => {
  const [ref, inView] = useInView();
  const F_STATS: FoundationStat[] = [
    { num: "500K+", lbl: "Children Helped" },
    { num: "120+",  lbl: "Projects Funded" },
    { num: "17",    lbl: "Years Active" },
    { num: "30+",   lbl: "Countries" },
  ];

  return (
    <section id="foundation" className="section" style={{ background: "linear-gradient(135deg,#0c0320 0%,#1a0635 50%,#0c0320 100%)", position: "relative", overflow: "hidden" }}>
      <div className="container">
        <div ref={ref} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 72, alignItems: "center" }}>
          {/* Image */}
          <div style={{ position: "relative", aspectRatio: "4/3", overflow: "hidden", opacity: inView ? 1 : 0, transform: inView ? "none" : "translateX(-40px)", transition: "opacity 0.7s,transform 0.7s" }}>
            <img src="https://images.unsplash.com/photo-1564415315949-7a0c4c73aab4?w=800&h=600&fit=crop&q=80" alt="Leo Messi Foundation" loading="lazy" style={{ width: "100%", height: "100%" }} />
            <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to right,transparent 55%,rgba(12,3,32,0.9))" }} />
          </div>

          {/* Content */}
          <div style={{ display: "flex", flexDirection: "column", gap: 20, opacity: inView ? 1 : 0, transform: inView ? "none" : "translateX(40px)", transition: "opacity 0.7s 0.15s,transform 0.7s 0.15s" }}>
            <div style={{ fontFamily: "var(--f-disp)", fontSize: "0.72rem", fontWeight: 500, letterSpacing: "0.32em", textTransform: "uppercase", color: "#e2b046", display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ background: "#e2b046", width: 26, height: 2, display: "block", flexShrink: 0 }} />
              Philanthropy
            </div>
            <h2 style={{ fontFamily: "var(--f-disp)", fontSize: "clamp(38px,5.5vw,78px)", fontWeight: 700, textTransform: "uppercase", lineHeight: 0.88 }}>
              Leo Messi<br />Foundation
            </h2>
            <p style={{ fontSize: "0.93rem", fontWeight: 300, lineHeight: 1.78, color: "var(--muted)", maxWidth: 480 }}>
              The Leo Messi Foundation guarantees access to education and healthcare for vulnerable children. Since 2007, it has touched the lives of hundreds of thousands of children across Argentina and beyond — because every child deserves a fair start.
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              {F_STATS.map((s, i) => (
                <div key={i} style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.07)", borderTop: "2px solid var(--pink)", padding: "16px 18px" }}>
                  <div style={{ fontFamily: "var(--f-disp)", fontSize: "2rem", fontWeight: 700, lineHeight: 1 }}>{s.num}</div>
                  <div style={{ fontFamily: "var(--f-disp)", fontSize: "0.62rem", fontWeight: 400, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--muted)", marginTop: 4 }}>{s.lbl}</div>
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Btn href="#foundation">Learn More</Btn>
              <Btn variant="outline" href="#foundation">Donate</Btn>
            </div>
          </div>
        </div>
      </div>
      <style>{`@media(max-width:900px){#foundation .container>div{grid-template-columns:1fr!important;gap:36px!important}}`}</style>
    </section>
  );
};

/* ─────────────────────────────────────────────
   QUOTE
───────────────────────────────────────────── */
const QuoteSection: FC = () => {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} style={{ padding: "100px 0", textAlign: "center", background: "var(--bg2)", opacity: inView ? 1 : 0, transform: inView ? "none" : "translateY(30px)", transition: "opacity 0.8s,transform 0.8s" }}>
      <div className="container">
        <div style={{ fontFamily: "var(--f-disp)", fontSize: "8rem", lineHeight: 0.42, color: "var(--pink)", opacity: 0.22, marginBottom: 18, fontWeight: 700 }}>"</div>
        <blockquote style={{ fontFamily: "var(--f-disp)", fontStyle: "italic", fontWeight: 300, fontSize: "clamp(1.35rem,2.5vw,2rem)", color: "var(--off)", maxWidth: 800, margin: "0 auto", lineHeight: 1.5 }}>
          Every year I try to grow as a player and not get stuck in a rut. I try to improve my game in every way possible. Hopefully the best is yet to come.
        </blockquote>
        <p style={{ fontFamily: "var(--f-disp)", fontSize: "0.76rem", fontWeight: 600, letterSpacing: "0.25em", textTransform: "uppercase", color: "var(--muted)", marginTop: 22 }}>— Lionel Messi</p>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   NEWSLETTER
───────────────────────────────────────────── */
const Newsletter: FC = () => {
  const [email, setEmail] = useState<string>("");
  const [done, setDone] = useState<boolean>(false);

  return (
    <div style={{ padding: "68px 0", background: "linear-gradient(135deg,var(--pink) 0%,#8c002e 100%)" }}>
      <div className="container" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 36, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ fontFamily: "var(--f-disp)", fontSize: "clamp(28px,4.5vw,54px)", fontWeight: 700, textTransform: "uppercase", lineHeight: 0.9, color: "#fff", marginBottom: 8 }}>
            Stay In<br />The Game
          </h2>
          <p style={{ fontSize: "0.88rem", color: "rgba(255,255,255,0.72)" }}>Exclusive news, behind-the-scenes &amp; foundation updates.</p>
        </div>
        {done ? (
          <div style={{ background: "rgba(255,255,255,0.16)", padding: "14px 26px", fontFamily: "var(--f-disp)", fontSize: "1rem", fontWeight: 600, color: "#fff", letterSpacing: "0.1em" }}>
            ✓ Subscribed — ¡Gracias!
          </div>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); if (email) setDone(true); }} style={{ display: "flex", maxWidth: 460, width: "100%" }}>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Your email address"
              style={{ flex: 1, padding: "0.7em 1.1em", background: "rgba(255,255,255,0.14)", border: "1px solid rgba(255,255,255,0.28)", borderRight: "none", color: "#fff", fontFamily: "var(--f-body)", fontSize: "0.9rem", outline: "none" }}
            />
            <button type="submit" style={{ padding: "0 1.6em", background: "#fff", border: "none", fontFamily: "var(--f-disp)", fontSize: "0.76rem", fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--pink)", cursor: "pointer", whiteSpace: "nowrap", transition: "background 0.2s" }}>
              Subscribe
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   FOOTER
───────────────────────────────────────────── */
const Footer: FC = () => (
  <footer style={{ background: "#030306", padding: "54px 0 26px" }}>
    <div className="container">
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 48, marginBottom: 48 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: "var(--pink)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--f-disp)", fontSize: "1.05rem", fontWeight: 700, color: "#fff" }}>M</div>
            <span style={{ fontFamily: "var(--f-disp)", fontSize: "1.05rem", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>Lionel Messi</span>
          </div>
          <div style={{ fontFamily: "var(--f-disp)", fontStyle: "italic", fontSize: "1.05rem", color: "var(--pink)", fontWeight: 300 }}>"El mejor de todos los tiempos"</div>
          <p style={{ fontSize: "0.78rem", fontWeight: 300, color: "var(--dim)", lineHeight: 1.75, maxWidth: 260 }}>
            Official website of Lionel Messi — 8× Ballon d'Or winner, FIFA World Cup champion &amp; Inter Miami captain.
          </p>
        </div>
        {FOOTER_COLS.map((col) => (
          <div key={col.title}>
            <div style={{ fontFamily: "var(--f-disp)", fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.28em", textTransform: "uppercase", color: "var(--pink)", marginBottom: 16 }}>{col.title}</div>
            {col.links.map((l) => (
              <a key={l} href="#" style={{ display: "block", fontSize: "0.78rem", fontWeight: 300, color: "rgba(255,255,255,0.32)", marginBottom: 9, transition: "color 0.2s" }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "#fff"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "rgba(255,255,255,0.32)"; }}
              >{l}</a>
            ))}
          </div>
        ))}
      </div>
      <div style={{ borderTop: "1px solid rgba(255,255,255,0.05)", paddingTop: 22, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <span style={{ fontSize: "0.7rem", fontWeight: 300, color: "rgba(255,255,255,0.2)" }}>© 2026 Leo Messi Foundation. All rights reserved.</span>
        <div style={{ display: "flex", gap: 8 }}>
          {(["IG", "TW", "FB", "YT"] as const).map((s) => (
            <div key={s} style={{ width: 33, height: 33, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.09)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "var(--f-disp)", fontSize: "0.68rem", fontWeight: 700, color: "var(--muted)", cursor: "pointer", transition: "background 0.2s,border-color 0.2s,color 0.2s" }}
              onMouseEnter={(e) => { const el = e.currentTarget as HTMLDivElement; el.style.background = "var(--pink)"; el.style.borderColor = "var(--pink)"; el.style.color = "#fff"; }}
              onMouseLeave={(e) => { const el = e.currentTarget as HTMLDivElement; el.style.background = "rgba(255,255,255,0.05)"; el.style.borderColor = "rgba(255,255,255,0.09)"; el.style.color = "var(--muted)"; }}
            >{s}</div>
          ))}
        </div>
      </div>
    </div>
    <style>{`@media(max-width:900px){footer .container>div:first-child{grid-template-columns:1fr 1fr!important}} @media(max-width:540px){footer .container>div:first-child{grid-template-columns:1fr!important}}`}</style>
  </footer>
);

/* ─────────────────────────────────────────────
   ROOT APP
───────────────────────────────────────────── */
export default function App(): JSX.Element {
  const [activeSection, setActiveSection] = useState<string>("hero");
  const scrollY = useScrollY();

  useEffect(() => {
    const mid = scrollY + window.innerHeight / 2;
    for (const id of SECTION_IDS) {
      const el = document.getElementById(id);
      if (el && el.getBoundingClientRect().top + scrollY - 100 <= mid) {
        setActiveSection(id);
        break;
      }
    }
  }, [scrollY]);

  return (
    <>
      <style>{STYLES}</style>
      <Navbar activeSection={activeSection} />
      <main>
        <Hero />
        <AwardsBand />
        <Bio />
        <Stats />
        <Honours />
        <NewsSection />
        <Gallery />
        <Foundation />
        <QuoteSection />
        <Newsletter />
      </main>
      <Footer />
    </>
  );
}

// import styles from "../assets/style.module.css";
// import { IProfileWebsite } from "../../../../services";

// export interface LandingProps {
//     website: IProfileWebsite;
// }

// function Landing(props: LandingProps) {

//     const { website } = props;

//     return (<div className={styles["txx"]}>
//         Hello WOrld sleeky - {website.username}
//     </div>);

// }

// export default Landing;
